import { faEllipsisVertical } from '@document-crunch/pro-regular-svg-icons'
import { Icon, IconColor } from '@/dc-shared-web/src/components/base/Icon'
import {
  DropdownMenu,
  Button,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ProjectNew,
  useFSTrackEvent,
  useAuth,
} from '@/dc-shared-web/src'
import { useState } from 'react'
import { RenameProjectDialog } from '@/app/(dashboard)/projects/RenameProjectDialog'
import { DeleteProjectDialog } from '@/app/(dashboard)/projects/DeleteProjectDialog'
import { MoveDialog } from '@/app/(dashboard)/projects/MoveDialog'
import { SELECTED_TEAM_ID_KEY } from '@dc-shared-web/lib/constants'
import { useSearchParams } from 'next/navigation'
import { userHasExplicitPermission } from '@/app/helpers/userHelper'
import { useQueryClient } from 'react-query'
import { FS } from '@/dc-shared-web/src/lib/trackingConstants'
import { LocationType } from '@/app/(dashboard)/projects/FolderSelectionTree'

export const ProjectActions = ({
  project,
  initialLocation,
}: {
  project: ProjectNew
  initialLocation: LocationType
}) => {
  const { user } = useAuth()
  const params = useSearchParams()
  const queryClient = useQueryClient()

  const selectedTeamId = params.get(SELECTED_TEAM_ID_KEY)

  const [selectedDialog, setSelectedDialog] = useState<
    'rename' | 'move' | 'delete' | null
  >(null)

  const { trackEvent } = useFSTrackEvent()

  const onClose = () => {
    setSelectedDialog(null)
  }

  const onSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['FolderForMove'],
    })

    setSelectedDialog(null)
  }

  const handleMoveProject = () => {
    trackEvent(FS.PROJECT_ACTIONS_MOVE_PROJECT, {
      project_id: project.uuid,
      project_name: project.name,
      team: selectedTeamId,
    })
    setSelectedDialog('move')
  }

  const isTeamEditor = () => {
    if (!selectedTeamId) {
      return false
    }

    return userHasExplicitPermission(user, {
      resource_uuid: selectedTeamId,
      role: 'EDITOR',
    })
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='mini'
            data-testid='project-actions-trigger'
          >
            <Icon
              icon={faEllipsisVertical}
              color={IconColor.tertiary}
              size='xl'
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={() => {
              setSelectedDialog('rename')
            }}
            data-testid={`project-action-rename`}
          >
            Rename project
          </DropdownMenuItem>
          {!isTeamEditor() && (
            <DropdownMenuItem
              onClick={() => {
                handleMoveProject()
              }}
              data-testid='project-action-move'
            >
              Move project
            </DropdownMenuItem>
          )}
          {!project.crunch_children_count &&
            !project.folder_children_count &&
            !isTeamEditor() && (
              <DropdownMenuItem
                onClick={() => {
                  setSelectedDialog('delete')
                }}
                data-testid='project-action-delete'
              >
                Delete project
              </DropdownMenuItem>
            )}
        </DropdownMenuContent>
      </DropdownMenu>
      {selectedDialog === 'rename' && (
        <RenameProjectDialog
          project={project}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
      {selectedDialog === 'move' && (
        <MoveDialog
          project={project}
          onClose={onClose}
          teamUuid={selectedTeamId || ''}
          onSuccess={onSuccess}
          type='project'
          open={selectedDialog === 'move'}
          initialLocation={initialLocation}
        />
      )}
      {selectedDialog === 'delete' && (
        <DeleteProjectDialog
          project={project}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
    </>
  )
}
